/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Index';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { getTermsPage } from '@/sanity/client';
import { format } from 'date-fns';
import { PortableText } from '@portabletext/react';

const TermsHeader = ({ header }: { header: any }) => {
	// Background image URL - use CMS data if available, otherwise fallback
	const backgroundImageUrl = header?.backgroundImage
		? header.backgroundImage
		: '/contact-banner.svg';

	// Overlay opacity - use CMS data if available, otherwise fallback to 50%
	const overlayOpacity =
		header?.overlayOpacity !== undefined ? header.overlayOpacity / 100 : 0.5;

	return (
		<section className=''>
			<div
				className='relative flex flex-col items-center px-16 pt-20 w-full bg max-md:px-5 max-md:max-w-full'
				style={{
					backgroundImage: `url(${backgroundImageUrl})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<div
					className='absolute inset-0 bg-black'
					style={{ opacity: overlayOpacity }}
				></div>
				<div className='relative z-10 mb-0 w-full max-w-[1215px] max-md:mb-2.5 max-md:max-w-full'>
					<div className='flex gap-5 max-md:flex-col py-4 md:px-10 px-5'>
						<div className='w-[65%] max-md:ml-0 max-md:w-full py-8'>
							<div className='mr-0 text-neutral-100 max-md:max-w-full'>
								<h1 className='text-5xl leading-[80px] max-md:max-w-full max-md:text-4xl max-md:leading-10'>
									{header?.title || 'Terms and Conditions'}
								</h1>
								<div className='mt-4 max-w-xl'>
									<p className='mt-6 text-3xl leading-10 max-md:max-w-full'>
										{header?.subtitle ||
											'Please read these terms and conditions carefully before using our services'}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

const Terms = () => {
	const [termsData, setTermsData] = useState<any>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchTermsData = async () => {
			try {
				const pageData = await getTermsPage();
				if (pageData) {
					setTermsData(pageData);
				}
			} catch (error) {
				console.error('Error fetching terms page data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchTermsData();
	}, []);

	// Default fallback terms in case CMS data isn't available
	const defaultTerms = [
		{
			title: '1. Introduction',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'Welcome to Yaap. By using our website, services, or products, you agree to comply with and be bound by the following terms and conditions. Please read these terms carefully before accessing or using our services.',
						},
					],
				},
			],
		},
		{
			title: '2. Changes to Terms',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We reserve the right to modify these terms at any time. Any changes will take effect immediately upon posting on our website.',
						},
					],
				},
			],
		},
		// ...other default terms
	];

	// Use CMS terms if available, otherwise use fallback
	const terms = termsData?.terms || defaultTerms;
	const lastUpdated = termsData?.lastUpdated
		? format(new Date(termsData.lastUpdated), 'MMMM yyyy')
		: '17 March 2025';

	if (loading) {
		return (
			<Layout>
				<div className='flex justify-center items-center min-h-screen'>
					<p>Loading terms and conditions...</p>
				</div>
			</Layout>
		);
	}

	return (
		<div className='flex flex-col w-full overflow-hidden'>
			<Layout>
				<main className='w-full'>
					<TermsHeader header={termsData?.header} />

					<section className='py-12 md:px-34 bg-background rounded-t-3xl relative z-20 -mt-5'>
						<div className='max-w-7xl md:px-15 px-10'>
							<div className='mb-10'>
								<h2 className='text-2xl font-semibold mb-6'>
									Our Terms and Conditions
								</h2>
								<p className='text-muted-foreground mb-8'>
									{termsData?.introduction ||
										`Last Updated: ${lastUpdated}. The following terms and conditions govern your use of our services and website.`}
								</p>
							</div>

							<Accordion type='single' collapsible className='w-full'>
								{terms.map((term: any, index: number) => (
									<AccordionItem
										key={index}
										value={`item-${index}`}
										className='border-b'
									>
										<AccordionTrigger className='text-left font-medium'>
											{term.title}
										</AccordionTrigger>
										<AccordionContent className='text-muted-foreground'>
											{Array.isArray(term.content) ? (
												<div className='py-2'>
													<PortableText value={term.content} />
												</div>
											) : (
												<p className='py-2'>{term.content}</p>
											)}
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>

							<div className='mt-12 pt-6 border-t border-gray-100'>
								<p className='text-sm text-muted-foreground'>
									{termsData?.footer ||
										'By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.'}
								</p>
							</div>
						</div>
					</section>
				</main>
			</Layout>
		</div>
	);
};

export default Terms;
